import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Send, MessageSquareText, BookText, Loader2, Flag, X } from "lucide-react";
import { toast } from "sonner";
import SpeakingTab from "@/components/practice/SpeakingTab";
import QuizTab from "@/components/practice/QuizTab";
import ListeningTab from "@/components/practice/ListeningTab";
import DictationTab from "@/components/practice/DictationTab";
import FreeTalkTab from "@/components/practice/FreeTalkTab";
import LevelSelect from "@/components/practice/LevelSelect";

function DialogueTab() {
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [level, setLevel] = useState("intermediate");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [session?.messages]);

  const startMutation = useMutation({
    mutationFn: () => api.post("/ai/dialogue/start", null, { params: { level } }),
    onSuccess: ({ data }) => {
      setSession(data);
      setAnalysis(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Не удалось начать диалог"),
  });

  const sendMutation = useMutation({
    mutationFn: (text) => api.post(`/ai/dialogue/${session.id}/message`, { message: text }),
    onSuccess: ({ data }) => {
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { role: "user", content: message }, { role: "assistant", content: data.reply }],
      }));
      setMessage("");
    },
  });

  const endMutation = useMutation({
    mutationFn: () => api.post(`/ai/dialogue/${session.id}/end`),
    onSuccess: ({ data }) => setAnalysis(data),
  });

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="dialogue-start-state">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <MessageSquareText className="w-7 h-7 text-primary" />
        </div>
        <p className="font-medium">Начните разговор с AI, используя изученные слова</p>
        <LevelSelect value={level} onValueChange={setLevel} testIdPrefix="dialogue" />
        <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending} data-testid="start-dialogue-button">
          {startMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Начать диалог"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[60vh]" data-testid="dialogue-active-state">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Тема: {session.topic}</p>
          <div className="flex gap-1 flex-wrap mt-1">
            {session.target_words.map((w) => <Badge key={w} variant="secondary">{w}</Badge>)}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => endMutation.mutate()} data-testid="end-dialogue-button">
          <Flag className="w-4 h-4 mr-1" /> Завершить
        </Button>
      </div>

      {analysis ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dialogue-analysis">
          <p className="font-heading font-semibold">Результаты разговора</p>
          <div>
            <p className="text-xs text-emerald-600 font-semibold mb-1">Использовано правильно</p>
            <div className="flex gap-1 flex-wrap">{analysis.used_correctly?.map((w) => <Badge key={w} className="bg-emerald-100 text-emerald-700 border-0">{w}</Badge>)}</div>
          </div>
          <div>
            <p className="text-xs text-orange-600 font-semibold mb-1">Использовано неправильно</p>
            <div className="flex gap-1 flex-wrap">{analysis.used_incorrectly?.map((w) => <Badge key={w} className="bg-orange-100 text-orange-700 border-0">{w}</Badge>)}</div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-semibold mb-1">Не использовано</p>
            <div className="flex gap-1 flex-wrap">{analysis.not_used?.map((w) => <Badge key={w} variant="secondary">{w}</Badge>)}</div>
          </div>
          <Button onClick={() => setSession(null)} data-testid="new-dialogue-button">Новый диалог</Button>
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dialogue-messages">
            {session.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
          <form
            className="p-3 border-t border-border flex gap-2"
            onSubmit={(e) => { e.preventDefault(); if (message.trim()) sendMutation.mutate(message); }}
          >
            <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Напишите ответ..." data-testid="dialogue-message-input" />
            <Button type="submit" disabled={sendMutation.isPending} data-testid="dialogue-send-button">
              {sendMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}

function extractSentenceContaining(text, phrase) {
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  const lower = phrase.toLowerCase();
  const found = sentences.find((s) => s.toLowerCase().includes(lower));
  return (found || sentences[0] || text).trim();
}

function StoryTab() {
  const [story, setStory] = useState(null);
  const [wordCount, setWordCount] = useState("10");
  const [difficulty, setDifficulty] = useState("medium");
  const [selectedPhrase, setSelectedPhrase] = useState("");
  const mutation = useMutation({
    mutationFn: () => api.post("/ai/story", null, { params: { count: wordCount, difficulty } }),
    onSuccess: ({ data }) => setStory(data),
    onError: (e) => toast.error(e.response?.data?.detail || "Не удалось создать историю"),
  });

  const addWordMutation = useMutation({
    mutationFn: (word) => api.post("/words/manual", { word, sentence: extractSentenceContaining(story.messages[0].content, word) }),
    onSuccess: (_, word) => {
      toast.success(`Слово "${word}" добавлено в словарь`);
      setSelectedPhrase("");
      window.getSelection()?.removeAllRanges();
    },
    onError: () => toast.error("Не удалось добавить слово"),
  });

  const handleStoryMouseUp = () => {
    const sel = window.getSelection();
    const text = sel?.toString().trim().replace(/\s+/g, " ");
    if (text && text.length > 1 && text.length < 60) {
      setSelectedPhrase(text.replace(/^[^\w']+|[^\w']+$/g, ""));
    }
  };

  return (
    <div className="space-y-4 py-4" data-testid="story-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Слов для истории:</span>
          <Select value={wordCount} onValueChange={setWordCount}>
            <SelectTrigger className="w-24" data-testid="story-word-count-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["5", "10", "15", "20"].map((c) => (
                <SelectItem key={c} value={c} data-testid={`story-word-count-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Сложность:</span>
          <Select value={difficulty} onValueChange={setDifficulty}>
            <SelectTrigger className="w-36" data-testid="story-difficulty-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="easy" data-testid="story-difficulty-easy">Лёгкая</SelectItem>
              <SelectItem value="medium" data-testid="story-difficulty-medium">Средняя</SelectItem>
              <SelectItem value="hard" data-testid="story-difficulty-hard">Сложная</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending} data-testid="generate-story-button">
          {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BookText className="w-4 h-4 mr-1" /> Сгенерировать историю</>}
        </Button>
      </div>

      {story && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-3 relative" data-testid="story-result">
          <h3 className="font-heading text-xl font-bold">{story.topic}</h3>
          <p className="leading-relaxed select-text" onMouseUp={handleStoryMouseUp} data-testid="story-text">
            {story.messages[0].content}
          </p>
          <div className="flex gap-1 flex-wrap pt-2">
            {story.analysis?.words_used?.map((w) => <Badge key={w} className="bg-primary/10 text-primary border-0">{w}</Badge>)}
          </div>

          {selectedPhrase && (
            <div
              className="sticky bottom-2 flex items-center justify-between gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-2 shadow-lg"
              data-testid="story-phrase-selection-bar"
            >
              <span className="text-sm font-medium truncate">«{selectedPhrase}»</span>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => addWordMutation.mutate(selectedPhrase)}
                  disabled={addWordMutation.isPending}
                  data-testid="story-add-phrase-button"
                >
                  {addWordMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Добавить в словарь"}
                </Button>
                <button onClick={() => setSelectedPhrase("")} data-testid="story-cancel-phrase-button" className="p-1.5">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AIPractice() {
  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="ai-practice-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">AI практика</h1>
        <p className="text-sm text-muted-foreground mt-1">Активируйте изученные слова в разговоре с AI</p>
      </div>

      <Tabs defaultValue="dialogue">
        <TabsList className="flex-wrap h-auto" data-testid="ai-practice-tabs">
          <TabsTrigger value="dialogue" data-testid="tab-dialogue">Разговор</TabsTrigger>
          <TabsTrigger value="speaking" data-testid="tab-speaking">Говорение</TabsTrigger>
          <TabsTrigger value="freetalk" data-testid="tab-freetalk">Свободный разговор</TabsTrigger>
          <TabsTrigger value="story" data-testid="tab-story">История</TabsTrigger>
          <TabsTrigger value="listening" data-testid="tab-listening">Аудирование</TabsTrigger>
          <TabsTrigger value="dictation" data-testid="tab-dictation">Диктант</TabsTrigger>
          <TabsTrigger value="quiz" data-testid="tab-quiz">Тренировка</TabsTrigger>
        </TabsList>
        <TabsContent value="dialogue" forceMount className="data-[state=inactive]:hidden"><DialogueTab /></TabsContent>
        <TabsContent value="speaking" forceMount className="data-[state=inactive]:hidden"><SpeakingTab /></TabsContent>
        <TabsContent value="freetalk" forceMount className="data-[state=inactive]:hidden"><FreeTalkTab /></TabsContent>
        <TabsContent value="story" forceMount className="data-[state=inactive]:hidden"><StoryTab /></TabsContent>
        <TabsContent value="listening" forceMount className="data-[state=inactive]:hidden"><ListeningTab /></TabsContent>
        <TabsContent value="dictation" forceMount className="data-[state=inactive]:hidden"><DictationTab /></TabsContent>
        <TabsContent value="quiz" forceMount className="data-[state=inactive]:hidden"><QuizTab /></TabsContent>
      </Tabs>
    </div>
  );
}
